import React, { Component } from 'react';
import { func, array, oneOf } from 'prop-types';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import {
  SnapaskLoader,
  CurrentPlanIcon,
  BillingHistoryIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@snapask/common-components';

import { camelcaseParser } from '@snapask/common-components/dist/utils/objCamelcaseParser';
import { yearMonthDayFormat } from '@dragonfruit/utils/dateParser';
import { isNeverExpired } from '@dragonfruit/utils/checkPlanExpire';

import { fetchPurchaseHistory } from '@dragonfruit/redux/purchaseHistory';
import { pauseSubscription } from '@dragonfruit/redux/modifySubscription';
import { actions as reportActions } from '@dragonfruit/redux/report';
import {
  premium as goToPremiumPage,
  goToChangePlanPage,
} from '@dragonfruit/redux/navigation';

import AccountCurrentSubscriptions from './components/AccountCurrentSubscriptions';
import AccountHistoricalPurchases from './components/AccountHistoricalPurchases';

import { NEWEB_MMK, NEWEB_ATM } from '@dragonfruit/constants/paymentTypes';
import { PAYMENT_PREORDER_PENDING } from '@dragonfruit/constants/paymentStatus';
import * as LOADING_STATE from '@dragonfruit/constants/loadingState';

import styles from './AccountSubscriptions.scss';
const cx = require('classnames/bind').bind(styles);

const mapStateToProps = state => ({
  historyList: state.purchaseHistory.historyList,
  currentPlanList: state.purchaseHistory.currentPlanList,
  purchaseHistoryStatus: state.purchaseHistory.loadingState,
});

const HISTORY_ROWS_PER_PAGE = 5;

const checkHasNext = (total, shown) => total - shown > 0;

class AccountSubscriptions extends Component {
  static propTypes = {
    fetchPurchaseHistory: func.isRequired,
    goToChangePlanPage: func.isRequired,
    goToPremiumPage: func.isRequired,
    pauseSubscription: func.isRequired,
    purchaseHistoryStatus: oneOf(Object.values(LOADING_STATE)).isRequired,
    setReportOptions: func.isRequired,
    t: func.isRequired,
    toggleReportForm: func.isRequired,
    currentPlanList: array,
    historyList: array,
  };

  static defaultProps = {
    historyList: [],
    currentPlanList: [],
  };

  state = {
    isHistoryDetailOpen: false,
    historyDetailId: 0,
    purchasesPerPage: HISTORY_ROWS_PER_PAGE,
    currentPage: 1,
    hasPrev: false,
    hasNext: checkHasNext(
      this.props.historyList.length,
      HISTORY_ROWS_PER_PAGE * 1
    ),
  };

  componentDidMount() {
    const { fetchPurchaseHistory } = this.props;

    fetchPurchaseHistory();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.historyList.length === this.props.historyList.length) return;
    this.onInitPagination();
  }

  onInitPagination = () => {
    this.setState({
      currentPage: 1,
      hasNext: checkHasNext(
        this.props.historyList.length,
        HISTORY_ROWS_PER_PAGE * 1
      ),
    });
  };

  onClickNext = () => {
    this.setState(prevState => ({
      currentPage: prevState.currentPage + 1,
      hasPrev: true,
      hasNext: checkHasNext(
        this.props.historyList.length,
        HISTORY_ROWS_PER_PAGE * (prevState.currentPage + 1)
      ),
    }));
  };

  onClickPrev = () => {
    this.setState(prevState => ({
      currentPage: prevState.currentPage - 1,
      hasPrev: prevState.currentPage - 1 !== 1,
      hasNext: true,
    }));
  };

  onToggleHistoryDetail = () => {
    this.setState(prevState => ({
      isHistoryDetailOpen: !prevState.isHistoryDetailOpen,
    }));
  };

  handleOpenHistoryDetail = id => () => {
    this.setState({ historyDetailId: id });
    this.onToggleHistoryDetail();
  };

  renderHistoryDetailItem = (title, value, className) => {
    if (!value) return;

    if (Array.isArray(value)) {
      return (
        <div className={styles.item}>
          <div className={styles.title}>{title}</div>
          <div className={cx('description', className)}>
            {value.map(text => (
              <p key={text} className={styles.descriptionText}>
                {text}
              </p>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className={styles.item}>
        <div className={styles.title}>{title}</div>
        <div className={cx('description', className)}>{value}</div>
      </div>
    );
  };

  renderSingleHistoryDetail = history => {
    const { t } = this.props;

    const {
      name,
      status: paymentStatus,
      startAt,
      expireAt,
      displayOriginalPrice,
      displaySellPrice,
      discountAmount,
      displayDiscountAmount,
      paymentType,
      deliveryInfo,
      newebDueDate,
      newebBankId,
      newebBankName,
      newebWriteoffNumber,
    } = history;

    const getExpireDate = (startAt, expireAt) => {
      if (paymentStatus === PAYMENT_PREORDER_PENDING) {
        return '-';
      }

      if (isNeverExpired(startAt, expireAt)) {
        return t('paymentReceipt:paymentReceipt.neverExpires');
      }

      return yearMonthDayFormat(expireAt);
    };

    const renderDetailByPaymentType = paymentType => {
      const isPayByNeweb =
        paymentType === NEWEB_MMK || paymentType === NEWEB_ATM;

      if (!isPayByNeweb)
        return this.renderHistoryDetailItem(
          t('paymentReceipt:paymentReceipt.nextBilling'),
          getExpireDate(startAt, expireAt)
        );

      if (!newebDueDate)
        return this.renderHistoryDetailItem(
          t('paymentReceipt:paymentReceipt.expiresOn'),
          getExpireDate(startAt, expireAt)
        );

      const newebPaymentInfoMap = {
        [NEWEB_MMK]: (
          <React.Fragment>
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.neweb.writeoffNumberTitle'),
              newebWriteoffNumber
            )}
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.dueDate'),
              yearMonthDayFormat(newebDueDate)
            )}
          </React.Fragment>
        ),
        [NEWEB_ATM]: (
          <React.Fragment>
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.neweb.bankTitle'),
              `${newebBankId} ${newebBankName}`
            )}
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.neweb.writeoffNumberTitle'),
              newebWriteoffNumber
            )}
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.dueDate'),
              yearMonthDayFormat(newebDueDate)
            )}
          </React.Fragment>
        ),
      };
      return newebPaymentInfoMap[paymentType];
    };

    return (
      <div className={styles.historyDetail}>
        {this.renderHistoryDetailItem(
          t('paymentReceipt:paymentReceipt.items'),
          name
        )}

        {this.renderHistoryDetailItem(
          t('paymentReceipt:paymentReceipt.itemAvailableDate'),
          yearMonthDayFormat(startAt)
        )}

        {renderDetailByPaymentType(paymentType)}

        {paymentType &&
          this.renderHistoryDetailItem(
            t('paymentReceipt:paymentReceipt.paymentType.title'),
            t(`paymentReceipt:paymentReceipt.paymentType.${paymentType}`)
          )}

        {!!discountAmount && (
          <React.Fragment>
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.originalPrice'),
              displayOriginalPrice
            )}
            {this.renderHistoryDetailItem(
              t('paymentReceipt:paymentReceipt.discountAmount'),
              displayDiscountAmount,
              'discount'
            )}
          </React.Fragment>
        )}

        {this.renderHistoryDetailItem(
          t('paymentReceipt:paymentReceipt.totalCost'),
          displaySellPrice
        )}

        {paymentStatus &&
          this.renderHistoryDetailItem(
            t('account.singlePayment.status'),
            t(`account.billingStatus.${camelcaseParser(paymentStatus)}`),
            paymentStatus
          )}

        {deliveryInfo &&
          this.renderHistoryDetailItem(
            t('purchase:purchase.deliveryInfo.title'),
            [
              deliveryInfo.name,
              deliveryInfo.phone,
              `${deliveryInfo.town.zipCode} ${deliveryInfo.city.name} ${deliveryInfo.town.name}`,
              deliveryInfo.address,
            ]
          )}
      </div>
    );
  };

  render() {
    const {
      t,
      currentPlanList,
      historyList,
      pauseSubscription,
      toggleReportForm,
      goToPremiumPage,
      setReportOptions,
      goToChangePlanPage,
      purchaseHistoryStatus,
    } = this.props;

    const {
      isHistoryDetailOpen,
      historyDetailId,
      purchasesPerPage,
      currentPage,
      hasPrev,
      hasNext,
    } = this.state;

    const targetHistoryDetail =
      historyList.find(history => history.id === historyDetailId) || {};

    const onClickChangePlan = (changePlanType, subscriptionId) => () =>
      goToChangePlanPage(changePlanType, subscriptionId);

    if (purchaseHistoryStatus !== LOADING_STATE.READY) {
      return <SnapaskLoader />;
    }

    return (
      <div className={styles.root}>
        <div className={styles.cardContainer}>
          {/* Subscription Card */}
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.text}>
                <CurrentPlanIcon width={40} height={40} />
                <h4>{t('account.currentSubscription')}</h4>
              </div>
            </div>
            <div className={styles.body}>
              <AccountCurrentSubscriptions
                subscriptions={currentPlanList}
                onClickChangePlan={onClickChangePlan}
                onPauseSubscription={pauseSubscription}
                toggleReportForm={toggleReportForm}
                setReportOptions={setReportOptions}
                onClickGoToPremiumPage={goToPremiumPage}
              />
            </div>
          </div>

          {/* Billing History Card */}
          {!isHistoryDetailOpen && historyList.length > 0 && (
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.text}>
                  <BillingHistoryIcon width={40} height={40} />
                  <h4>{t('account.billingHistory')}</h4>
                </div>
                <div className={styles.buttons}>
                  <button
                    className={cx(styles.prevArrow, { disabled: !hasPrev })}
                    onClick={this.onClickPrev}
                  >
                    <ChevronLeftIcon width={24} height={24} />
                  </button>
                  <button
                    className={cx(styles.nextArrow, { disabled: !hasNext })}
                    onClick={this.onClickNext}
                  >
                    <ChevronRightIcon width={24} height={24} />
                  </button>
                </div>
              </div>
              <div className={styles.body}>
                <AccountHistoricalPurchases
                  purchases={historyList}
                  currentPage={currentPage}
                  purchasesPerPage={purchasesPerPage}
                  onClickPurchase={this.handleOpenHistoryDetail}
                />
                <div className={styles.mobileButtons}>
                  <button
                    className={cx(styles.prevArrow, { disabled: !hasPrev })}
                    onClick={this.onClickPrev}
                  >
                    <ChevronLeftIcon width={24} height={24} />
                  </button>
                  <button
                    className={cx(styles.nextArrow, { disabled: !hasNext })}
                    onClick={this.onClickNext}
                  >
                    <ChevronRightIcon width={24} height={24} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {
            /* history detail */
            <div
              className={cx('paymentInfoCard', {
                hide: !isHistoryDetailOpen,
              })}
            >
              <div className={styles.historyDetailContainer}>
                <div className={styles.closeHistoryDetail}>
                  <a onClick={this.onToggleHistoryDetail}>
                    {t('account.back')}
                  </a>
                </div>
                {this.renderSingleHistoryDetail(targetHistoryDetail)}

                <div className={styles.explainIntroductoryPrice}>
                  {t('account.explainIntroductoryPrice')}
                </div>
                <div className={styles.closeHistoryDetailMobile}>
                  <a onClick={this.onToggleHistoryDetail}>
                    {t('account.back')}
                  </a>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    );
  }
}

export default translate(['account', 'paymentReceipt'])(
  connect(mapStateToProps, {
    pauseSubscription,
    fetchPurchaseHistory,
    toggleReportForm: reportActions.toggleReportForm,
    setReportOptions: reportActions.setReportOptions,
    goToPremiumPage,
    goToChangePlanPage,
  })(AccountSubscriptions)
);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountSubscriptions/AccountSubscriptions.jsx